import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SetupLayout from "../SetupLayout/SetupLayout";
import { useSetupState, useSetupDispatch, setUser, resetSetup } from "../../../auth/SetupContext";
import { API_BASE_URL } from "../../../auth/config";
import CustomDropdown from "../../../components/customComponents/customDropdown/CustomDropdown";
import formStyles from "../setupForm.module.scss";

const GENDER_OPTIONS = [
  { id: "Male", label: "Male" },
  { id: "Female", label: "Female" },
  { id: "Other", label: "Other" },
];

const STATUS_OPTIONS = [
  { id: "1", label: "Active" },
  { id: "0", label: "Inactive" },
];

const initialForm = {
  username: "",
  email: "",
  password: "",
  mobileNumber: "",
  address: "",
  gender: "Male",
  status: "1",
};

export default function RegisterUser() {
  const navigate = useNavigate();
  const dispatch = useSetupDispatch();
  const { companyId, roles } = useSetupState();

  const [form, setForm] = useState(initialForm);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!companyId) navigate("/setup/company", { replace: true });
  }, [companyId, navigate]);

  const handleBack = () => {
    navigate("/", { replace: true });
    setTimeout(() => dispatch(resetSetup()), 0);
  };

  const validateField = (name, value) => {
    switch (name) {
      case "username":
        return value.trim() ? "" : "Username is required.";
      case "email":
        if (!value.trim()) return "Email is required.";
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
          ? ""
          : "Please enter a valid email address.";
      case "password":
        if (!value.trim()) return "Password is required.";
        return value.length >= 8 ? "" : "Password must be at least 8 characters.";
      default:
        return "";
    }
  };

  const validateRoles = (roleIds) =>
    roleIds.length ? "" : "Select at least one role.";

  const isFormValid =
    !validateField("username", form.username) &&
    !validateField("email", form.email) &&
    !validateField("password", form.password) &&
    !validateRoles(selectedRoleIds);

  const update = (field) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [field]: value }));
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
  };

  const updateDropdown = (field) => (item) => {
    setForm((f) => ({ ...f, [field]: item.id }));
    setFieldErrors((prev) => ({ ...prev, [field]: validateField(field, item.id) }));
  };

  const toggleRole = (roleId) => {
    setSelectedRoleIds((prev) => {
      const next = prev.includes(roleId)
        ? prev.filter((id) => id !== roleId)
        : [...prev, roleId];
      setFieldErrors((current) => ({ ...current, roles: validateRoles(next) }));
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const nextFieldErrors = {
      username: validateField("username", form.username),
      email: validateField("email", form.email),
      password: validateField("password", form.password),
      roles: validateRoles(selectedRoleIds),
    };
    setFieldErrors(nextFieldErrors);

    if (Object.values(nextFieldErrors).some(Boolean)) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        ...form,
        mobileNumber: form.mobileNumber ? Number(form.mobileNumber) : undefined,
        companyId,
        status: Number(form.status),
        roles: selectedRoleIds,
      };
      const res = await axios.post(`${API_BASE_URL}/api/users/register`, payload);
      dispatch(setUser(res.data.id, res.data.username));
      navigate("/setup/user-access", { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || "Failed to register user");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SetupLayout
      step={5}
      totalSteps={6}
      title="Register user"
      subtitle="Create the first admin user for this company."
      error={error}
      onBack={handleBack}
      backDisabled={isSubmitting}
      loading={isSubmitting}
      loadingText="Registering user..."
    >
      <form onSubmit={handleSubmit} className={formStyles.body}>
        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="username">Username</label>
            <input id="username" className={formStyles.input} value={form.username} onChange={update("username")} required />
            {fieldErrors.username && <span className={formStyles.fieldError}>{fieldErrors.username}</span>}
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="email">Email</label>
            <input id="email" type="email" className={formStyles.input} value={form.email} onChange={update("email")} required />
            {fieldErrors.email && <span className={formStyles.fieldError}>{fieldErrors.email}</span>}
          </div>
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="password">Password</label>
            <input id="password" type="password" className={formStyles.input} value={form.password} onChange={update("password")} required />
            {fieldErrors.password && <span className={formStyles.fieldError}>{fieldErrors.password}</span>}
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label} htmlFor="mobileNumber">Mobile number</label>
            <input id="mobileNumber" className={formStyles.input} value={form.mobileNumber} onChange={update("mobileNumber")} />
          </div>
        </div>

        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label} htmlFor="address">Address</label>
          <input id="address" className={formStyles.input} value={form.address} onChange={update("address")} />
        </div>

        <div className={formStyles.row}>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label}>Gender</label>
            <CustomDropdown
              data={GENDER_OPTIONS}
              value={form.gender}
              className={formStyles.dropdownWrap}
              triggerClassName={formStyles.dropdownTrigger}
              showArrow
              onSelect={updateDropdown("gender")}
            />
          </div>
          <div className={formStyles.fieldGroup}>
            <label className={formStyles.label}>Status</label>
            <CustomDropdown
              data={STATUS_OPTIONS}
              value={form.status}
              className={formStyles.dropdownWrap}
              triggerClassName={formStyles.dropdownTrigger}
              showArrow
              onSelect={updateDropdown("status")}
            />
          </div>
        </div>

        <div className={formStyles.fieldGroup}>
          <label className={formStyles.label}>Roles</label>
          {roles.length === 0 && <div className={formStyles.readOnlyValue}>No roles created yet.</div>}
          {roles.map((role) => (
            <label key={role.id ?? role.name} className={formStyles.checkboxRow}>
              <input
                type="checkbox"
                checked={selectedRoleIds.includes(role.id)}
                onChange={() => toggleRole(role.id)}
              />
              {role.name}
            </label>
          ))}
          {fieldErrors.roles && <span className={formStyles.fieldError}>{fieldErrors.roles}</span>}
        </div>

        <div className={formStyles.btnRow}>
          <button type="submit" className={formStyles.primaryBtn} disabled={isSubmitting || !isFormValid}>
            {isSubmitting ? "Registering..." : "Register user"}
          </button>
        </div>
      </form>
    </SetupLayout>
  );
}
